import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Input } from "../components/ui/input";

const PRICE = {
  starter_monthly: process.env.REACT_APP_STRIPE_PRICE_STARTER_MONTHLY,
  starter_yearly: process.env.REACT_APP_STRIPE_PRICE_STARTER_YEARLY,
  pro_monthly: process.env.REACT_APP_STRIPE_PRICE_PRO_MONTHLY,
  pro_yearly: process.env.REACT_APP_STRIPE_PRICE_PRO_YEARLY,
  agency_monthly: process.env.REACT_APP_STRIPE_PRICE_AGENCY_MONTHLY,
  agency_yearly: process.env.REACT_APP_STRIPE_PRICE_AGENCY_YEARLY,
};

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    priceMonthly: "$29/mo",
    priceYearly: "$290/yr",
    highlights: ["Export enabled", "Studio access (credits)", "1 workspace", "Great for solo brands"],
  },
  {
    id: "pro",
    name: "Pro",
    priceMonthly: "$79/mo",
    priceYearly: "$790/yr",
    highlights: ["Export enabled", "More generations/month", "3 workspaces", "Better scaling"],
  },
  {
    id: "agency",
    name: "Agency",
    priceMonthly: "$199/mo",
    priceYearly: "$1,990/yr",
    highlights: ["Export enabled", "50 workspaces", "Client-ready workflows", "White-label later"],
  },
];

export default function UpgradePage() {
  const location = useLocation();
  const [usage, setUsage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [err, setErr] = useState(null);
  const [billingContext, setBillingContext] = useState(null);

  // Founder Test Mode
  const [adminKey, setAdminKey] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const isAdmin = usage?.is_admin || usage?.plan === "admin";
  const isTrial = !isAdmin && usage?.plan === "trial";
  const displayPlan = isAdmin ? "Admin" : usage?.effective_plan || usage?.plan || "—";
  const campaignCheckout = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("source") !== "campaign_wallet") return null;
    const adBudget = Number(params.get("ad_budget") || 0);
    return {
      campaignId: params.get("campaign") || "",
      plan: params.get("plan") || "campaign",
      adBudget: Number.isFinite(adBudget) ? adBudget : 0,
    };
  }, [location.search]);

  const localOnly = useMemo(() => {
    if (typeof window === "undefined") return true;
    return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  }, []);

  useEffect(() => {
    (async () => {
      setErr(null);
      setBillingContext(null);
      try {
        const billing = await api.get("/api/billing/provider");
        if (!["shopify", "stripe"].includes(billing.data?.provider)) throw new Error("Unknown billing provider");
        setBillingContext(billing.data);
        const u = await api.get("/api/me/usage");
        setUsage(u.data);
        setTestEmail(u.data.email);

        if (billing.data.provider === "shopify") return;
        const p = await api.get("/api/credits/packages");
        setPackages(p.data.items || []);
        const checkoutState = new URLSearchParams(location.search).get("credits");
        if (checkoutState === "success") {
          setMsg("Payment received. Stripe is adding the credits to your balance automatically.");
        } else if (checkoutState === "canceled") {
          setMsg("Credit purchase canceled. You were not charged.");
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load upgrade data.");
      }
    })();
  }, [location.search]);

  async function adminSetPlan(plan) {
    if (!localOnly) return;
    setMsg(null);
    setBusy(true);
    try {
      await api.post(
        "/api/admin/set-plan",
        { email: testEmail, plan, bonus_credits: 0 },
        { headers: { "X-Admin-Key": adminKey } }
      );
      const u = await api.get("/api/me/usage");
      setUsage(u.data);
      setMsg(`Plan updated to "${plan}".`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Admin set-plan failed. Check ADMIN_API_KEY and backend logs.");
    } finally {
      setBusy(false);
    }
  }

  async function adminBuyCredits(package_id) {
    if (!localOnly) return;
    setMsg(null);
    setBusy(true);
    try {
      await api.post(
        "/api/credits/purchase",
        {
          email: testEmail,
          package_id,
          payment_provider: "manual",
          payment_ref: "founder_test_mode",
        },
        { headers: { "X-Admin-Key": adminKey } }
      );
      const u = await api.get("/api/me/usage");
      setUsage(u.data);
      setMsg(`Credits added from package "${package_id}".`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Credit purchase failed. Check ADMIN_API_KEY and backend logs.");
    } finally {
      setBusy(false);
    }
  }

  async function goCheckout(price_id) {
    if (billingContext?.provider !== "stripe") return;
    setErr(null);
    try {
      const res = await api.post("/api/billing/checkout-session", { price_id });
      window.location.href = res.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to open checkout.");
    }
  }

  async function goCreditCheckout(package_id) {
    if (billingContext?.provider !== "stripe") return;
    setErr(null);
    setMsg(null);
    setBusy(true);
    try {
      const res = await api.post("/api/billing/credit-checkout-session", { package_id });
      window.location.href = res.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to open secure credit checkout.");
      setBusy(false);
    }
  }

  async function goPortal() {
    if (billingContext?.provider !== "stripe") return;
    setErr(null);
    try {
      const res = await api.post("/api/billing/portal-session");
      window.location.href = res.data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to open billing portal.");
    }
  }

  // Never flash external purchase controls while billing routing is unknown.
  if (!billingContext) return (
    <Card data-testid="billing-provider-pending">
      <CardHeader><CardTitle>Billing</CardTitle></CardHeader>
      <CardContent role="status">{err || "Verifying your billing provider…"}</CardContent>
    </Card>
  );

  if (billingContext.provider === "shopify") return (
    <div className="space-y-6" data-testid="shopify-billing-page">
      <Card>
        <CardHeader><CardTitle>Choose or manage your plan in Shopify</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p>Installation connects your store; it does not select or approve a paid plan. Review the current prices and approve any charge on Shopify’s hosted plan page.</p>
          <p data-testid="shopify-effective-plan">Current effective OmniMark access: <strong>{displayPlan}</strong>. Credits: {usage?.credits ?? "—"}. Effective access can include a separately verified existing entitlement; this is not proof of a paid Shopify subscription.</p>
          <div className="grid gap-3 md:grid-cols-3" data-testid="shopify-plan-features">
            {PLANS.map((plan) => <div key={plan.id} className="rounded-lg border p-3"><h2 className="font-semibold">{plan.name}</h2><p className="text-sm">{plan.id === "agency" ? "50 workspaces" : plan.id === "pro" ? "3 workspaces" : "1 workspace"} · Export and Studio access, subject to credits and feature availability.</p><p className="text-xs text-muted-foreground">Prices, trials and available billing cycles are confirmed in Shopify—not the regular Stripe offer.</p></div>)}
          </div>
          <p className="text-sm">The same named plan uses the same OmniMark feature gates in either channel. Prices, billing cycles and extra credit-pack availability can differ. No plan is selected here.</p>
          {err && <p role="alert">{err}</p>}
          {(billingContext.shops || []).map((shop) => (
            <div key={shop.shop} className="rounded-xl border p-4 space-y-2">
              <div className="font-semibold">{shop.name}</div>
              <div className="text-sm text-muted-foreground">{shop.shop}</div>
              {shop.pricing_url ? (
                <a href={shop.pricing_url} target="_top" className="inline-block rounded-md border px-4 py-2" data-testid="shopify-plan-selection">View plans in Shopify</a>
              ) : (
                <p role="status">{shop.connected ? "Shopify plan selection is not configured yet. Contact OmniMark support; no checkout has been opened." : "Reconnect this store before managing its Shopify plan."}</p>
              )}
            </div>
          ))}
          {!billingContext.shops?.length && <p role="status">Reconnect your Shopify store to manage its plan. External checkout is unavailable for this account.</p>}
          <p className="text-sm text-muted-foreground">Extra credit-pack purchases are unavailable in this channel until Shopify billing for them is implemented. You will not be redirected to Stripe.</p>
          <Link to="/app/connections">Store connections</Link>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="upgrade-page">
      <div>
        <div className="text-2xl font-semibold">Upgrade</div>
        <div className="text-sm text-muted-foreground">
          Unlock exporting + Studio tools. Scale to Pro or Agency when you're ready.
        </div>
      </div>

      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      {msg && !localOnly && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-3 text-sm text-emerald-800">{msg}</CardContent>
        </Card>
      )}

      {campaignCheckout && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader>
            <CardTitle>Campaign payment checkout</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              Suggested ad-platform budget: <strong>${campaignCheckout.adBudget.toLocaleString()}</strong>
            </div>
            <div className="text-muted-foreground">
              OmniMark separates ad-platform spend from your OmniMark subscription and generation credits. Fund a campaign only after its connected ad account, budget, and approval path are clear.
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Ad spend: pass-through budget</Badge>
              <Badge variant="outline">AI usage: plan + credits</Badge>
              <Badge variant="outline">Managed growth fee: optional</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Status */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card data-testid="plan-status">
          <CardHeader><CardTitle>Plan</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="text-xl font-medium">{displayPlan}</div>
              {isAdmin && <Badge variant="outline">Owner access</Badge>}
              {isTrial && <Badge variant="secondary">Trial</Badge>}
              {!isAdmin && !isTrial && usage?.plan && <Badge variant="outline">Paid</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">Monthly + yearly supported</div>
          </CardContent>
        </Card>

        <Card data-testid="credits-status">
          <CardHeader><CardTitle>Credits</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            <div className="text-xl font-medium">{usage?.credits ?? "—"}</div>
            <div className="text-xs text-muted-foreground">Used for generations, Avatar Studio, edits, and scheduled content</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{isAdmin ? "Access" : "Trial Ends"}</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            <div className="text-sm">
              {isAdmin
                ? "Owner bypass enabled"
                : usage?.trial_ends_at
                  ? new Date(usage.trial_ends_at).toLocaleString()
                  : "—"}
            </div>
            <div className="text-xs text-muted-foreground">
              {isAdmin ? "All features and limits unlocked" : "Upgrade anytime"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* What's locked */}
      {isTrial && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Trial limitations <Badge variant="secondary">Export locked</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              You can generate ad packs during trial, but exporting and Studio tools unlock on paid plans.
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Export</Badge>
              <Badge variant="outline">Studio</Badge>
              <Badge variant="outline">Multiple workspaces</Badge>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => goCheckout(PRICE.starter_monthly)}
                data-testid="upgrade-now-btn"
              >
                Upgrade Now
              </Button>
              <Link to="/app/ads">
                <Button variant="outline">Generate Ad Pack</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Plans</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          {PLANS.map((p) => {
            const isCurrentPlan = usage?.plan === p.id;

            return (
              <div key={p.id} className={`rounded-xl border bg-background p-4 ${isCurrentPlan ? "ring-2 ring-primary" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">{p.name}</div>
                  {isCurrentPlan ? (
                    <Badge variant="default">Current</Badge>
                  ) : (
                    <Badge variant="outline">{p.id}</Badge>
                  )}
                </div>

                <div className="mt-2 space-y-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{p.priceMonthly}</span>
                    <span className="text-sm text-muted-foreground">or {p.priceYearly}</span>
                  </div>
                </div>

                <Separator className="my-3" />

                <ul className="space-y-2 text-sm">
                  {p.highlights.map((h) => (
                    <li key={h} className="rounded-md border p-2">{h}</li>
                  ))}
                </ul>

                <div className="mt-3">
                  {isCurrentPlan ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={goPortal}
                      data-testid={`manage-${p.id}-btn`}
                    >
                      Manage Subscription
                    </Button>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => goCheckout(
                          p.id === "starter" ? PRICE.starter_monthly :
                          p.id === "pro" ? PRICE.pro_monthly :
                          PRICE.agency_monthly
                        )}
                        data-testid={`choose-${p.id}-monthly-btn`}
                      >
                        Monthly
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => goCheckout(
                          p.id === "starter" ? PRICE.starter_yearly :
                          p.id === "pro" ? PRICE.pro_yearly :
                          PRICE.agency_yearly
                        )}
                        data-testid={`choose-${p.id}-yearly-btn`}
                      >
                        Yearly
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Manage Subscription for paid users */}
      {!isTrial && usage?.plan && (
        <Card>
          <CardHeader>
            <CardTitle>Billing & Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Manage your subscription, update payment methods, or view billing history.
            </div>
            <Button onClick={goPortal} data-testid="manage-subscription-btn">
              Open Billing Portal
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Credit packages */}
      <Card>
        <CardHeader>
          <CardTitle>Credit charge-ups</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Credits power generations, Avatar Studio, customer edits, and scheduled content. Buy securely through Stripe; credits are added automatically after payment.
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {packages.map((pkg) => (
              <div key={pkg.package_id} className="rounded-xl border bg-background p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{pkg.package_id}</div>
                  <Badge variant="secondary">${pkg.price_usd}</Badge>
                </div>
                <div className="mt-2 text-2xl font-medium">{pkg.credits} credits</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Best for extra generations & Studio usage.
                </div>

                <div className="mt-3 flex gap-2">
                  <Button
                    className="w-full"
                    onClick={() => goCreditCheckout(pkg.package_id)}
                    disabled={busy}
                    data-testid={`buy-${pkg.package_id}`}
                  >
                    Buy securely
                  </Button>

                  {/* Founder local test button */}
                  {localOnly && (
                    <Button
                      variant="outline"
                      onClick={() => adminBuyCredits(pkg.package_id)}
                      disabled={busy || !adminKey}
                      title="Local-only founder test mode"
                      data-testid={`test-${pkg.package_id}`}
                    >
                      Test
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Founder Test Mode */}
      {localOnly && (
        <Card className="border-slate-200" data-testid="founder-test-mode">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Founder Test Mode
              <Badge variant="outline">Local only</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-muted-foreground">
              This is for your internal testing while Stripe isn't live yet. Don't use this in production.
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-2 md:col-span-1">
                <label className="text-sm font-medium">Admin key</label>
                <Input
                  placeholder="ADMIN_API_KEY"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  data-testid="admin-key-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">Test email</label>
                <Input
                  placeholder="user@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  data-testid="test-email-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" disabled={busy || !adminKey} onClick={() => adminSetPlan("trial")} data-testid="set-trial-btn">
                Set Trial
              </Button>
              <Button variant="outline" disabled={busy || !adminKey} onClick={() => adminSetPlan("starter")} data-testid="set-starter-btn">
                Set Starter
              </Button>
              <Button variant="outline" disabled={busy || !adminKey} onClick={() => adminSetPlan("pro")} data-testid="set-pro-btn">
                Set Pro
              </Button>
              <Button variant="outline" disabled={busy || !adminKey} onClick={() => adminSetPlan("agency")} data-testid="set-agency-btn">
                Set Agency
              </Button>
              <Link to="/app/library">
                <Button disabled={busy} className="ml-auto">Test Export</Button>
              </Link>
            </div>

            {msg && (
              <div className="rounded-lg border bg-muted p-3 text-sm" data-testid="test-message">
                {msg}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
